import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Timeline, TimelineConnector, TimelineDot, TimelineItem, TimelineOppositeContent, TimelineSeparator } from "@mui/lab";
import { Box, ClickAwayListener, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { produce } from "immer";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { DashboardContext } from "../../../contexts/DashboardContext";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import { getDashboardQueriesFunc } from "../../../lib/helper";
import { scrollbarStyle } from '../../../shared/dashboard';
import theme from "../../../theme";
import { Dashboard, DashboardQuery, EmailBody } from "../../../types/files";
import { EmailSummary } from "../../../types/search";
import ContentActivityFeed from "../../molecules/home-companies/ContentActivityFeed";
import { ReactComponent as TimeForwardIcon } from "../../../assets/icons/time-forward.svg";

const useStyles = makeStyles((theme) => ({
    title: {
        fontFamily: 'Inter',
        fontSize: '1.3rem !important',
        fontWeight: 'bold',
        color: 'black',
    },
    activity: {
        overflowX: 'hidden',
        overflowY: 'auto',
        zIndex: 1300,
        ...scrollbarStyle,
    },
    feed: {
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        ...scrollbarStyle,
    },
    connector : {
        background: theme.colors.neutral['300'],
    },
    content : {
        padding: '0px !important',
        paddingRight: '24px !important',
    },
}));

const BoxShadow = '-8px 8px 12px rgba(0, 0, 0, 0.15), -16px 16px 14px rgba(0, 0, 0, 0.1), -24px 24px 16px rgba(0, 0, 0, 0.075), -32px 32px 24px rgba(0, 0, 0, 0.05)';
const Height = 'calc(100vh - 140px)';

const DashboardSidebar: React.FC<{
    expanded: boolean;
    onExpand?: (expanded: boolean) => void;
}> = ({ expanded, onExpand }) => {
    const classes = useStyles();
    const { mappedOverviewQueries, setDashboardsQueries } = useContext(DashboardsContext);
    const { dashboard, isPublicView } = useContext(DashboardContext);
    const [emailSummaryLoaded, setEmailSummaryLoaded] = useState<boolean>(false);

    const isJSON = (str: string) => { try { return typeof JSON.parse(str) === 'object' && JSON.parse(str) !== null; } catch { return false; } };

    const sortedActivities = useMemo(() => {
        const emailSummaries = mappedOverviewQueries.get(`${dashboard!.id}:Email Summaries`) as EmailSummary[];
        const activityItems: {
            dashboard: Dashboard,
            emailBody?: EmailBody & { summary?: string, threads?: ({ timestamp: string|null, sender?: string, message: string })[] },
            createdAt: string, type: 'attached' | 'created'
        }[] = [];

        if (!!dashboard) {
            activityItems.push({ dashboard, createdAt: String(dashboard.createdAt), type: 'created', });
            dashboard?.emailBodies?.forEach(emailBody => activityItems.push({ dashboard,
                emailBody: ({ ...emailBody,
                    summary: Array.isArray(emailSummaries) ? (emailSummaries?.find(s => s?.id === emailBody?.id)?.summary || '') : '',
                    threads: emailBody?.content?.split('\n')
                        .map((msgBlock: string) => isJSON(msgBlock) ? JSON.parse(msgBlock.trim() || '') : msgBlock)
                }),
                createdAt: String(emailBody.createdAt), type: 'attached',
            }));
        }

        return activityItems.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }, [dashboard, mappedOverviewQueries]);

    useEffect(() => {
        if (!emailSummaryLoaded) {
            Promise.resolve(
                getDashboardQueriesFunc(dashboard!.id, 'Email Summaries')
            ).then((bulkQueryData: DashboardQuery[]) => {
                setDashboardsQueries(prev => produce(prev, draft => {
                    bulkQueryData.forEach(newQuery => {
                        if (!draft.find(query => query.dashboardId === newQuery.dashboardId && query.title === newQuery.title))
                            draft.push(newQuery);
                    });
                }));
                setEmailSummaryLoaded(true);
            });
        }
    // eslint-disable-next-line
    }, [dashboard]);

    return (<>
        {expanded ? (<>
            <ClickAwayListener onClickAway={(e) => { e.stopPropagation(); onExpand?.(false); }}>
                <Stack className={classes.activity} width="100%" height={Height} p={1} sx={{ boxShadow: BoxShadow }}>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" my={1}>
                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start" ml={2} py={1}>
                            <TimeForwardIcon style={{ width: 24, height: 24, fill: '#aeaeae' }} />
                            <Typography className={classes.title} ml={1}>
                                {`Timeline for `}
                                <span style={{ color: theme.palette.primary.main }}>
                                    {dashboard?.title}
                                </span>
                            </Typography>
                        </Stack>
                        <Box mr="16px !important">
                            <IconButton onClick={(e) => {
                                e.stopPropagation();
                                onExpand?.(false);
                            }}> <ArrowForwardIosIcon /> </IconButton>
                        </Box>
                    </Stack>
                    <Divider sx={{ width: '100%' }} />
                    <Stack direction="column" className={classes.feed} alignItems="stretch">
                        <Timeline position="right">
                            {sortedActivities.map((activity, i) => (
                                <TimelineItem sx={{ minHeight: 'unset !important' }}
                                    key={`activity-feed-116-${i}-` + activity.dashboard.id}>
                                    <TimelineOppositeContent className={classes.content}>
                                        <ContentActivityFeed activity={activity} vertical />
                                    </TimelineOppositeContent>
                                    <TimelineSeparator>
                                        <TimelineConnector className={classes.connector} />
                                        <TimelineDot sx={{ background: theme.colors.neutral['300'], BoxShadow: 'unset'}} />
                                        <TimelineConnector className={classes.connector} />
                                    </TimelineSeparator>
                                </TimelineItem>
                            ))}
                            <TimelineItem sx={{ minHeight: 'unset !important', height: '100%' }}>
                                <TimelineOppositeContent className={classes.content} />
                                <TimelineSeparator sx={{ height: '100%' }}>
                                    <Box width={12} />
                                    <TimelineConnector className={classes.connector} sx={{ height: '100%' }} />
                                </TimelineSeparator>
                            </TimelineItem>
                        </Timeline>
                    </Stack>
                </Stack>
            </ClickAwayListener>
        </>) : !isPublicView && (<>
            <Divider orientation="vertical" sx={{ height: Height, marginTop: '-8x !important' }} />
            <Stack direction="column" className={classes.activity} spacing={3} alignItems="center" mt="18px" px={3}
                sx={{ width: 'fit-content', height: '100%', cursor: 'pointer'}}
                onClick={(e) => {
                    e.stopPropagation();
                    onExpand?.(true);
                }}>
                <IconButton onClick={(e) => {
                    e.stopPropagation();
                    onExpand?.(true);
                }}> <ArrowBackIosIcon /> </IconButton>
                <Tooltip placement="left" title={
                    <Typography fontFamily="Inter">
                        {`Timeline for `}
                        <span>{dashboard?.title}</span>
                    </Typography>
                } componentsProps={{tooltip:{sx:{
                    display: 'flex', alignItems: 'center', backgroundColor: 'rgba(150, 150, 150, 0.9)',
                    height: '28px', padding: "20px",
                }}}}>
                <TimeForwardIcon style={{ width: 24, height: 24, fill: '#aeaeae' }} />
            </Tooltip>
            </Stack>
        </>)}
    </>);
}

export default DashboardSidebar;